/**
 *
 * @module sdkSessionConfiguration
 *
 */

import Check from 'check-types';
import Logging from './../../logging/entry';

import ServiceDefinitionsConfiguration from './serviceDefinitionsConfiguration';
import LoggerConfiguration from './loggerConfiguration';
import ConsoleSinkConfiguration from './../../logging/consoleSinkConfiguration';
import LoggingClientConfiguration from './../../logging/loggingClientConfiguration';
import EnvironmentConfiguration from './../platforms/environmentConfiguration';

import InvalidArgumentException from './../exception/invalidArgumentException';

const {
    nonEmptyObject,
    instanceStrict
} = Check.assert;

/**
 *
 * @class SdkSessionConfiguration
 * @desc Provides configuration information necessary to create an SdkSession instance.
 *
 */
export default class SdkSessionConfiguration {

    /**
     *
     * @param {Object} commonHeaders
     * @param {Object} loggerConfiguration
     * @param {Object} services
     * @param {SDK.Services.Configuration.EnvironmentConfiguration} environmentConfiguration
     * @param {SDK.Logging.Logger} [logger=Logging]
     *
     */
    constructor(commonHeaders, loggerConfiguration, services, environmentConfiguration, logger = Logging) {

        const errorMessage = {
            ctor: 'SdkSessionConfiguration(commonHeaders, services, environmentConfiguration, logger)',
            get commonHeaders() {
                return `${this.ctor} commonHeaders should be a non-empty Object`;
            },
            get loggerConfiguration() {
                return `${this.ctor} loggerConfiguration should be a non-empty Object`;
            },
            get services() {
                return `${this.ctor} services should be a non-empty Object`;
            },
            get environmentConfiguration() {
                return `${this.ctor} needs EnvironmentConfiguration instance`;
            }
        };

        nonEmptyObject(commonHeaders, errorMessage.commonHeaders, InvalidArgumentException);
        nonEmptyObject(loggerConfiguration, errorMessage.loggerConfiguration, InvalidArgumentException);
        nonEmptyObject(services, errorMessage.services, InvalidArgumentException);

        instanceStrict(
            environmentConfiguration,
            EnvironmentConfiguration,
            errorMessage.environmentConfiguration,
            InvalidArgumentException
        );

        /**
         *
         * @access public
         * @type {Object}
         * @desc Gets or sets the collection of headers to be used for all service requests.
         *
         */
        this.commonHeaders = {};

        /**
         *
         * @access public
         * @type {SDK.Services.Configuration.LoggerConfiguration}
         * @desc Gets or sets the configuration information necessary for logging.
         * @note Property matches loggerConfiguration class name instead of using the object name from the config.
         *
         */
        this.loggerConfiguration = {};

        /**
         *
         * @access public
         * @type {SDK.Services.Configuration.ServiceDefinitionsConfiguration}
         *
         */
        this.services = {};

        /**
         *
         * @desc process plain config Object and create configuration instances
         *
         */
        this.processConfig({
            commonHeaders,
            loggerConfiguration,
            services
        },
        environmentConfiguration,
        logger);

    }

    /* #region private: */

    /**
     *
     * @access private
     * @param {Object} config JSON configuration object returned from configuration service endpoint.
     * @param {SDK.Services.Configuration.EnvironmentConfiguration} environmentConfiguration
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @desc Initialize SdkSessionConfiguration by processing config object.
     *
     */
    processConfig(config, environmentConfiguration, logger = Logging) {

        const { commonHeaders, loggerConfiguration, services } = config;
        const { sdkVersion, platform } = environmentConfiguration;

        this.commonHeaders = commonHeaders;

        const versionHeader = this.commonHeaders['X-BAMSDK-Version'].replace(/\{SDKVersion\}/gi, sdkVersion);
        const platformHeader = this.commonHeaders['X-BAMSDK-Platform'].replace(/\{SDKPlatform\}/gi, platform);

        this.commonHeaders['X-BAMSDK-Version'] = versionHeader;
        this.commonHeaders['X-BAMSDK-Platform'] = platformHeader;

        /**
         *
         * @type {SDK.Logging.LoggingClientConfiguration}
         *
         */
        const loggingClientConfiguration = new LoggingClientConfiguration('', {});

        /**
         *
         * @type {SDK.Services.Configuration.LoggerConfiguration}
         *
         */
        this.loggerConfiguration = new LoggerConfiguration(
            new ConsoleSinkConfiguration(loggerConfiguration.logLevel),
            loggingClientConfiguration
        );

        /**
         *
         * @type {SDK.Services.Configuration.ServiceDefinitionsConfiguration}
         *
         */
        this.services = new ServiceDefinitionsConfiguration(services, this.commonHeaders, logger);

    }

}



// WEBPACK FOOTER //
// ./src/services/configuration/sdkSessionConfiguration.js